<template>
  <div title="Short Answer interaction" class="question-node shortanswer-node" :class="{ selected: isSelected, collapsed: isCollapsed }">
    <!-- En-tête -->
    <div class="node-header">
      <button @click="toggleCollapse" class="collapse-btn" title="Réduire / développer">
        <span class="caret" :class="{ rotated: isCollapsed }">▲</span>
      </button>

      <p class="question-text">
        <span class="node-type-badge">SA</span>
        <span class="textQuestioHeader"> {{ truncatedText || 'Short Answer sans texte' }}</span>
      </p>

      <div class="d-flex justify-content-between align-items-center">
        <button hidden class="btn btn-sm btn-outline-danger" @click.stop.prevent="onDelete">✕</button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="node-toolbar">
      <button class="toolbar-btn edit" @click="onOpenEditor" title="Éditer le contenu">
        <i class="fas fa-pen"></i>
      </button>

      <button class="toolbar-btn delete" @click.stop="onDelete" title="Supprimer le nœud">
        <i class="fas fa-trash"></i>
      </button>
    </div>

    <!-- Contenu du nœud avec animation -->
    <transition name="fade-slide">
      <div v-if="!isCollapsed" class="node-content">
        <div class="shortanswer-info">
          <!-- Badge basé sur le type de correction -->
          <span v-if="hasCorrectionGrid" class="badge bg-primary">Grid</span>
          <span v-else-if="hasRefText" class="badge bg-info">Ref-Text</span>
          <span v-else class="badge bg-secondary">Free</span>

          <span v-if="element?.maxScore" class="badge bg-success ms-2">{{ element.maxScore }} pts</span>
        </div>
      </div>
    </transition>

    <!-- Handle d'entrée (gauche) - masqué si nested -->
    <Handle
      v-if="!parentNode"
      id="input"
      type="target"
      :position="Position.Left"
      class="input-handle"
    />

    <!-- Handle de sortie (droite) - masqué si nested -->
    <Handle
      v-if="!parentNode"
      id="output"
      type="source"
      :position="Position.Right"
      class="output-handle"
      :is-valid-connection="isValidOutgoingConnection"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { useTestStore } from '../../../stores/testStore'
import { useVueFlow } from '@vue-flow/core'

const { edges, setEdges, nodes } = useVueFlow()

const props = defineProps({
  id: String,
  data: { type: Object, default: () => ({}) },
  selected: Boolean,
  element: { type: Object, default: null },
  parentNode: { type: String, default: null }
})

const emit = defineEmits(['delete', 'update', 'open-editor'])
const testStore = useTestStore()

// --- Gestion du collapse (local + synchro)
const isCollapsed = ref(props.data?.collapsed ?? false)

watch(() => props.data?.collapsed, (val) => {
  isCollapsed.value = !!val
})

const toggleCollapse = async (event) => {
  event.stopPropagation()
  event.preventDefault()
  isCollapsed.value = !isCollapsed.value
  emit('update', { id: props.id, data: { collapsed: isCollapsed.value } })
  await nextTick()
}

const truncatedText = computed(() => {
  const text = props.element?.el_Text || 'Short Answer sans texte'
  if (!text) return ''
  return text.length > 30 ? text.slice(0, 30) + '…' : text
})

// --- Détection du type de correction
const hasCorrectionGrid = computed(() => {
  return props.element?.useGrid === true &&
         Array.isArray(props.element?.correctionAxes) &&
         props.element.correctionAxes.length > 0
})

const hasRefText = computed(() => {
  return props.element?.hasTextReference === true &&
         props.element?.refText &&
         props.element.refText.trim().length > 0
})

// --- Actions
function onDelete() {
  if (window.confirm('⚠️ Are you sure you want to delete this node? \nThis action cannot be undone.')) {
    emit('delete', { nodeId: props.id, elementId: props.data?.elementId })
  }
}

function onOpenEditor() {
  emit('open-editor', { nodeId: props.id, elementId: props.data?.elementId })
}

// --- Sélection
const isSelected = computed(() => props.selected === true)

// --- Validation des connexions sortantes (une seule connexion autorisée)
function isValidOutgoingConnection(connection) {
  const allEdges = edges.value || []

  // Vérifier si ce node a déjà une connexion sortante
  const existingOutgoingConnections = allEdges.filter(
    edge => edge.source === props.id
  )

  if (existingOutgoingConnections.length > 0) {
    console.log('🚫 ShortAnswerNode: Une seule connexion sortante est autorisée')
    return false
  }

  return true
}
</script>

<style scoped>
.question-node {
  background: white;
  border: 2px solid #ddd;
  border-radius: 8px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
}

.question-node.collapsed {
  min-width: 220px;
  max-width: 260px;
}

.question-node.selected {
  border-color: #0d6efd;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  transform: translateY(-2px);
}

/* Header */
.node-header {
  background-color: #f8f9fa;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  padding: 4px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.collapse-btn:hover {
  color: #0d6efd;
}

.caret {
  display: inline-block;
  transition: transform 0.25s ease;
  font-size: 12px;
}

.caret.rotated {
  transform: rotate(180deg);
}

/* Toolbar */
.node-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 20px;
}

.toolbar-btn {
  border: none;
  background: #f8f9fa;
  color: #555;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.toolbar-btn:hover {
  background: #e9ecef;
  color: #0d6efd;
  transform: translateY(-1px);
}

.toolbar-btn:active {
  transform: scale(0.95);
}

/* Contenu */
.node-content {
  padding: 15px;
}

.shortanswer-info {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* Transition contenu */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.question-text {
  position: absolute;
  margin-left: 30px;
  margin-top: 21px;
}

.textQuestioHeader {
  margin-left: 0px;
  color: #0056b3;
}

/* Handles */
.input-handle {
  width: 16px !important;
  height: 16px !important;
  background-color: #6c757d !important;
  border: 2px solid white !important;
  border-radius: 50% !important;
  transform: translateX(-50%);
}

.output-handle {
  width: 16px !important;
  height: 16px !important;
  background-color: #20c997 !important;
  border: 2px solid white !important;
  border-radius: 50% !important;
  transform: translateX(50%);
}

.input-handle:hover,
.output-handle:hover {
  transform: scale(1.2);
}

/* Badge type SA */
.node-type-badge {
  display: inline-block;
  background: #20c997;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 5px;
  border-radius: 3px;
  margin-right: 6px;
}

.shortanswer-node {
  border-color: #20c997;
}

.shortanswer-node.selected {
  border-color: #20c997;
  box-shadow: 0 4px 12px rgba(32, 201, 151, 0.3);
}
</style>
